/**
 * GuillocheHalo — React wrapper for the GuillocheRenderer engine.
 */

import { useRef, useEffect } from 'react';
import { GuillocheRenderer } from '../engine/guilloche';
import type { HaloConfig } from '../engine/guilloche';

interface GuillocheHaloProps {
  config: HaloConfig;
  size: number;
  className?: string;
  style?: React.CSSProperties;
}

export function GuillocheHalo({ config, size, className, style }: GuillocheHaloProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<GuillocheRenderer | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const renderer = new GuillocheRenderer(canvas, config);
    rendererRef.current = renderer;
    renderer.start();

    const obs = new ResizeObserver(() => renderer.resize());
    obs.observe(canvas);

    return () => {
      renderer.dispose();
      obs.disconnect();
      rendererRef.current = null;
    };
  }, [config]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{
        width: size,
        height: size,
        maxWidth: 'none',
        pointerEvents: 'none',
        ...style,
      }}
    />
  );
}

export default GuillocheHalo;
